import React, { useState } from 'react';

import {
  convertAmount,
  deriveRate,
  formatDate,
  isValidDecimalInput,
  missingReferenceRate,
  normalizeDecimalInput,
} from '@splitfiesta/core';
import type { ConversionState } from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';
import { EXCHANGE_RATE_MAX, formatCurrencyWithCode } from '@splitfiesta/shared';
import { FormField } from '@splitfiesta/ui';

import { LinkButton } from '@/components/ui/LinkButton';

import { formatExchangeRate } from '@/utils/exchangeRate';

export interface ConversionPreviewInfo {
  status: 'idle' | 'loading' | 'error' | 'success';
  /** The provider's reference rate, when a preview has resolved. */
  rate?: number | null;
  /** The date the reference rate applies to. */
  rateDate?: string | null;
  error?: unknown;
  refetch?: () => void;
}

interface ConversionPreviewProps {
  groupCurrency: string;
  enteredCurrency: string;
  /** What the user typed, in `enteredCurrency`. */
  enteredAmount: number;
  conversion: ConversionState;
  preview: ConversionPreviewInfo;
  onManualRateChange: (rate: number | null) => void;
  /** `conversion.pendingConversion` — foreign entry queued offline, no rate yet. */
  isOfflineQueue: boolean;
  disabled?: boolean;
  /** The expense date: the offline-queued message, and whether the rate
      is the latest published close rather than that day's. */
  date: string;
}

/**
 * Preview line under the amount field for a foreign-currency expense:
 * shows the converted total, the applied rate and its date, and lets the
 * user override the rate with a manual value (or a manually-typed
 * converted total, from which the rate is derived). Rendered only while
 * `conversion.isForeign` — the parent (`ExpenseBasicInfoPresentation`)
 * gates it.
 */
export function ConversionPreview({
  groupCurrency,
  enteredCurrency,
  enteredAmount,
  conversion,
  preview,
  onManualRateChange,
  isOfflineQueue,
  disabled = false,
  date,
}: ConversionPreviewProps) {
  const { t, i18n } = useTranslation('expenses');
  const [isEditing, setIsEditing] = useState(false);
  const [rateInput, setRateInput] = useState('');
  const [totalInput, setTotalInput] = useState('');
  const [rateOutOfRange, setRateOutOfRange] = useState(false);

  // A currency switch invalidates any in-progress override: the picker
  // lives in the sibling component, so this is the only signal we get
  // that the previous rate/amount context is gone. Reset during render
  // (React's documented pattern for clearing state on a prop change)
  // rather than in an effect, so the stale editor never flashes.
  const currencyKey = `${enteredCurrency}:${groupCurrency}`;
  const [lastCurrencyKey, setLastCurrencyKey] = useState(currencyKey);
  if (currencyKey !== lastCurrencyKey) {
    setLastCurrencyKey(currencyKey);
    setIsEditing(false);
    setRateInput('');
    setTotalInput('');
    setRateOutOfRange(false);
  }

  const locale = i18n.language;
  const formatRate = (rate: number) => formatExchangeRate(rate, locale);
  // No locale: amounts follow the currency's own convention, like every
  // other amount in the app.
  const formatAmount = (amount: number, currency: string) =>
    formatCurrencyWithCode(amount, currency);
  // Plain-numeric (no locale grouping/decimal separators) so the value is
  // always parsable by a numeric <input> — unlike `formatRate`, which is
  // for display only.
  const toInputRate = (rate: number) => String(Number(rate.toPrecision(6)));

  const startEditing = () => {
    setRateInput(
      conversion.effectiveRate !== null
        ? toInputRate(conversion.effectiveRate)
        : ''
    );
    setTotalInput(
      conversion.effectiveRate !== null ? String(conversion.numericAmount) : ''
    );
    setRateOutOfRange(false);
    setIsEditing(true);
  };

  const useReferenceRate = () => {
    setIsEditing(false);
    setRateInput('');
    setTotalInput('');
    setRateOutOfRange(false);
    onManualRateChange(null);
  };

  // Closes the editor while keeping whatever override is already in
  // effect (set as the user typed, via `handleRateInputChange`/
  // `handleTotalInputChange` below) — unlike `useReferenceRate`, this
  // must NOT call `onManualRateChange`.
  const closeEditor = () => {
    setIsEditing(false);
  };

  // Text input + `normalizeDecimalInput` rather than type=number +
  // `parseFloat`: a number input hands "1,08" over as "" in locales whose
  // decimal separator is ".", which used to clear the override silently.
  const handleRateInputChange = (value: string) => {
    if (!isValidDecimalInput(value)) return;
    setRateInput(value);
    setRateOutOfRange(false);
    const parsed = normalizeDecimalInput(value);
    // Empty or zero is an explicit "clear the override"; a lone "." is
    // mid-typing and must not touch the override yet.
    if (!value.trim() || parsed === 0) {
      onManualRateChange(null);
      setTotalInput('');
      return;
    }
    if (isNaN(parsed)) return;
    if (parsed > EXCHANGE_RATE_MAX) {
      setRateOutOfRange(true);
      onManualRateChange(null);
      setTotalInput('');
      return;
    }
    onManualRateChange(parsed);
    setTotalInput(String(convertAmount(enteredAmount, parsed, groupCurrency)));
  };

  const handleTotalInputChange = (value: string) => {
    if (!isValidDecimalInput(value)) return;
    setTotalInput(value);
    setRateOutOfRange(false);
    const parsedTotal = normalizeDecimalInput(value);
    if (!value.trim() || isNaN(parsedTotal)) return;
    if (parsedTotal === 0) {
      onManualRateChange(null);
      setRateInput('');
      return;
    }
    const rate = deriveRate(enteredAmount, parsedTotal);
    if (rate === null) return;
    if (rate > EXCHANGE_RATE_MAX) {
      setRateOutOfRange(true);
      onManualRateChange(null);
      setRateInput('');
      return;
    }
    onManualRateChange(rate);
    setRateInput(toInputRate(rate));
  };

  // Renders the "conversion-line" summary in all its states (offline
  // queued, resolved, loading, error). `showActions` omits the
  // Edit/Retry links while the editor is already open — see `isEditing`
  // below, which renders this line ABOVE the editor so the user never
  // loses sight of the current amount/rate while overriding it.
  function renderPreviewLine(showActions: boolean) {
    const sharedProps = {
      'data-testid': 'conversion-line',
      role: 'status' as const,
      'aria-live': 'polite' as const,
    };

    if (isOfflineQueue) {
      return (
        <div
          {...sharedProps}
          className="mt-2 text-sm flex flex-wrap items-center gap-x-2 gap-y-1"
        >
          <span className="text-gray-500 dark:text-gray-400">
            {t('conversion.offlineQueued', { date: formatDate(date, locale) })}
          </span>
          {showActions && (
            <LinkButton size="sm" onClick={startEditing} disabled={disabled}>
              {t('conversion.editRate')}
            </LinkButton>
          )}
        </div>
      );
    }

    if (conversion.effectiveRate !== null) {
      const isManual = conversion.rateSource === 'manual';
      return (
        <div
          {...sharedProps}
          className="mt-2 text-sm flex flex-wrap items-center gap-x-2 gap-y-1"
        >
          <span className="basis-full text-gray-900 dark:text-gray-100 font-medium">
            {t('conversion.previewAmount', {
              amount: formatAmount(conversion.numericAmount, groupCurrency),
            })}
          </span>
          {/* pre-line: the market-rate strings break before the rate. */}
          <span className="whitespace-pre-line text-gray-500 dark:text-gray-400">
            {isManual
              ? t('conversion.previewRateManual', {
                  rate: formatRate(conversion.effectiveRate),
                })
              : t(
                  conversion.rateDate && conversion.rateDate !== date
                    ? 'conversion.previewRateLatest'
                    : 'conversion.previewRate',
                  {
                    rate: formatRate(conversion.effectiveRate),
                    date: conversion.rateDate
                      ? formatDate(conversion.rateDate, locale)
                      : '',
                  }
                )}
          </span>
          {isManual && (
            <span
              data-testid="manual-badge"
              className="text-xs rounded-full px-2 py-0.5 bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
            >
              {t('conversion.manualBadge')}
            </span>
          )}
          {showActions && (
            <div className="basis-full">
              <LinkButton size="sm" onClick={startEditing} disabled={disabled}>
                {t('conversion.editRate')}
              </LinkButton>
            </div>
          )}
        </div>
      );
    }

    if (preview.status === 'loading') {
      return (
        <div
          {...sharedProps}
          className="mt-2 text-sm text-gray-500 dark:text-gray-400"
        >
          {t('conversion.loading')}
        </div>
      );
    }

    if (preview.status === 'error') {
      const missing = missingReferenceRate(preview.error);
      let message = t('conversion.unavailable');
      if (missing?.reason === 'currency') {
        message = t('conversion.unavailableForCurrency', {
          currency: missing.currency,
        });
      } else if (missing?.reason === 'beforeCoverage') {
        message = t('conversion.unavailableBeforeCoverage', {
          date: formatDate(missing.coverageStart, locale),
        });
      }
      return (
        <div
          {...sharedProps}
          className="mt-2 text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2"
        >
          <span>{message}</span>
          {showActions && !missing && (
            <LinkButton
              size="sm"
              onClick={() => preview.refetch?.()}
              disabled={disabled}
            >
              {t('conversion.retry')}
            </LinkButton>
          )}
          {showActions && (
            <LinkButton size="sm" onClick={startEditing} disabled={disabled}>
              {t('conversion.editRate')}
            </LinkButton>
          )}
        </div>
      );
    }

    return null;
  }

  if (isEditing) {
    return (
      <>
        {renderPreviewLine(false)}
        <div data-testid="conversion-editor" className="mt-2 space-y-2 text-sm">
          <p className="font-medium text-gray-700 dark:text-gray-300">
            {t('conversion.manualRate')}
          </p>
          <div className="grid grid-cols-2 gap-2">
            <FormField
              id="conversion-rate"
              type="text"
              inputMode="decimal"
              label={t('conversion.rateLabel', { from: enteredCurrency })}
              helpText={t('conversion.originalHint', {
                amount: formatAmount(enteredAmount, enteredCurrency),
              })}
              error={
                rateOutOfRange ? t('conversion.rateOutOfRange') : undefined
              }
              value={rateInput}
              onChange={handleRateInputChange}
              disabled={disabled}
            />
            <FormField
              id="conversion-converted-total"
              type="text"
              inputMode="decimal"
              label={t('conversion.convertedLabel', {
                currency: groupCurrency,
              })}
              value={totalInput}
              onChange={handleTotalInputChange}
              disabled={disabled}
            />
          </div>
          {preview.status === 'success' && typeof preview.rate === 'number' && (
            <p
              data-testid="reference-rate"
              className="text-xs text-gray-500 dark:text-gray-400"
            >
              {t(
                preview.rateDate && preview.rateDate !== date
                  ? 'conversion.referenceRateLatest'
                  : 'conversion.referenceRate',
                {
                  from: enteredCurrency,
                  to: groupCurrency,
                  rate: formatRate(preview.rate),
                  date: preview.rateDate
                    ? formatDate(preview.rateDate, locale)
                    : '',
                }
              )}
            </p>
          )}
          <p
            data-testid="conversion-source-hint"
            className="text-xs text-gray-500 dark:text-gray-400"
          >
            {t('conversion.sourceHint')}
          </p>
          <div className="flex items-center justify-between">
            <LinkButton
              size="sm"
              onClick={useReferenceRate}
              disabled={disabled}
            >
              {t('conversion.useReferenceRate')}
            </LinkButton>
            <LinkButton size="sm" onClick={closeEditor} disabled={disabled}>
              {t('conversion.done')}
            </LinkButton>
          </div>
        </div>
      </>
    );
  }

  return renderPreviewLine(true);
}

export default ConversionPreview;
